import { useState, useEffect } from "react";
import { Avatar, Typography, Spin } from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import axiosInstance from "../services/axios";
import "./AdministrativeLayout.css";

// Replace with your actual assets
import mapLines from "../assets/images/maplines.png";
import mapPlain from "../assets/images/mapPlain.png";
import buildingSvg from "../assets/images/buildings.png"; // optional SVG file
import Heading from "./Heading";
const { Text } = Typography;

const getImageUrl = (image, defaultFolder = 'administration') => {
  if (!image) return null;
  if (typeof image !== 'string') return null;

  if (image.startsWith('http://') || image.startsWith('https://')) {
    if (image.includes('localhost') || image.includes('127.0.0.1')) {
      const mediaIdx = image.indexOf('/media/');
      if (mediaIdx !== -1) return image.substring(mediaIdx);
    }
    if (window.location.protocol === 'https:' && image.startsWith('http:')) {
      return image.replace(/^http:/, 'https:');
    }
    return image;
  }

  if (image.startsWith('/media/')) return image;
  if (image.startsWith('/')) return `/media${image}`;
  if (image.includes('/')) return `/media/${image}`;
  return `/media/${defaultFolder}/${image}`;
};

const AdministrativeLayout = ({ className = "" }) => {
  const { t, i18n } = useTranslation();

  const initialAdministrative = [];
  const [administrativeData, setAdministrativeData] = useState(
    initialAdministrative
  );
  const [loadingAdmin, setLoadingAdmin] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const languageParam = i18n.language === "mr" ? "Marathi" : "English";
    setLoadingAdmin(true);

    axiosInstance
      .get("/administration/", {
        params: { tags: "State",  language: languageParam },
      })
      .then((resp) => {
        if (!isMounted) return;
        let data = resp && resp.data ? resp.data : [];
        if (!Array.isArray(data)) {
          data = data.results || data.data || Object.values(data) || [];
        }

        if (!Array.isArray(data) || data.length === 0) {
          setAdministrativeData(initialAdministrative);
          return;
        }

        // If `order` (or similar) exists on all items, sort by it; otherwise preserve API order
        const orderKeyCandidates = ["order", "sort_order", "position"];
        const orderKey = orderKeyCandidates.find((k) =>
          data.every((item) => item[k] !== undefined)
        );
        const ordered = orderKey
          ? [...data].sort(
              (a, b) => (Number(a[orderKey]) || 0) - (Number(b[orderKey]) || 0)
            )
          : data;

        const mapped = ordered.map((d) => ({
          id: d.id,
          name: d.name || "-",
          title: d.designation || d.title || "-",
          avatarUrl: getImageUrl(d.image, 'administration'),
          contact: d.contact || null,
          whatsapp: d.whatsapp || null,
          tags: d.tags || null,
        }));

        setAdministrativeData(mapped.length ? mapped : initialAdministrative);
      })
      .catch((err) => {
        console.error("Failed to load administration data:", err);
        if (isMounted) setAdministrativeData(initialAdministrative);
      })
      .finally(() => {
        if (isMounted) setLoadingAdmin(false);
      });

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);
  return (
    <div>
      <div className={`admin-wrap ${className}`.trim()}>
        <img loading="lazy" src={mapLines} alt="Decorative line art" className="lineart" />

        <Heading content={t("administrative.title") || "महाराष्ट्र शासन"} />

        <div className="admin-flex max-md-flex-col gap-28">
          <div className="admin-flex admin-flex-col admin-flex-1">
            {loadingAdmin ? (
              <div style={{ padding: 24 }}>
                <Spin />
              </div>
            ) : (
              administrativeData.map((admin, index) => (
                <div
                  key={admin.id || index}
                  className="admin-relative admin-flex admin-flex-col admin-w-full max-w-md"
                >
                  {/* Card */}
                  <div className="admin-flex admin-w-full admin-max-w-md align-items-center">
                    <Avatar
                      size={window.innerWidth < 768 ? 72 : 80}
                      src={admin.avatarUrl}
                      icon={<UserOutlined />}
                      className="admin-avatar"
                    />
                    <div className="admin-flex admin-flex-col admin-justify-center admin-ml-negative-50 admin-border admin-border-white admin-bg-white admin-pl-16 admin-rounded-full admin-w-full admin-max-w-md">
                      <Text className="text-[17px] text-[#212121] font-semibold">
                        {admin.name}
                      </Text>
                      <Text className="admin-text-gray admin-w-full text-[14px] text-[#727272] font-medium leading-20">
                        {admin.title}
                      </Text>               
                    </div>
                  </div>

                  {/* Line (except after the last card) */}
                  {index !== administrativeData.length - 1 && (
                    <div className="admin-top-full admin-w-2-5 admin-h-7 admin-bg-black admin-self-center" />
                  )}
                </div>
              ))
            )}
          </div>
          <div className="admin-right-col">
            <div className="illustration">
              <img
                src={mapPlain}
                alt={t("administrative.mapAlt") || "भारत नकाशा"}
                className="map"
              />
              {buildingSvg ? (
                <img
                  src={buildingSvg}
                  alt={t("administrative.buildingAlt") || "इमारत"}
                  className="building"
                />
              ) : (
                <div className="building-placeholder">
                  {t("administrative.buildingPlaceholder") || "सरकारी इमारत"}
                </div>
              )}
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export default AdministrativeLayout;
